import {
  CheckCircle2,
  Clock,
  CreditCard,
  FileCheck2,
  GraduationCap,
  Laptop,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import type { MemberCompany } from "../types";

type StepState = "done" | "current" | "pending" | "rejected";

function stateClass(state: StepState) {
  if (state === "done") return "border-emerald-200 bg-emerald-50 text-emerald-800";
  if (state === "current") return "border-amber-200 bg-amber-50 text-amber-800";
  if (state === "rejected") return "border-red-200 bg-red-50 text-red-800";
  return "border-slate-200 bg-slate-50 text-slate-600";
}

function stepStatus(member: MemberCompany, step: string): StepState {
  const status = member.status;

  if (status === "BOARD_REJECTED" || status === "REJECTED") return step === "board" ? "rejected" : "pending";
  if (status === "SUSPENDED") return step === "active" ? "rejected" : "done";

  const order = [
    "created",
    "board",
    "payment",
    "ict",
    "training",
    "active",
  ];

  const statusIndex =
    status === "PROSPECT" || status === "PENDING_REVIEW" ? 0 :
    status === "BOARD_PENDING" ? 1 :
    status === "BOARD_APPROVED" || status === "PAYMENT_PENDING" ? 2 :
    status === "PAYMENT_CONFIRMED" || status === "ICT_PENDING" ? 3 :
    status === "ICT_COMPLETED" || status === "TRAINING_PENDING" ? 4 :
    status === "TRAINING_COMPLETED" || status === "ACTIVE_MEMBER" ? 5 :
    0;

  const stepIndex = order.indexOf(step);

  if (stepIndex < statusIndex) return "done";
  if (stepIndex === statusIndex) return status === "ACTIVE_MEMBER" ? "done" : "current";
  return "pending";
}

export function MembershipWorkflowTimeline({ member }: { member: MemberCompany }) {
  const steps = [
    {
      key: "created",
      label: "Prospect / Member Created",
      description: "Record captured from website, manual entry or upload.",
      icon: FileCheck2,
    },
    {
      key: "board",
      label: "Board Approval",
      description: "Trade Manager records board decision.",
      icon: ShieldCheck,
    },
    {
      key: "payment",
      label: "Payment / Subscription",
      description: "Finance confirms fees, levies and subscriptions.",
      icon: CreditCard,
    },
    {
      key: "ict",
      label: "ICT Account Setup",
      description: "ICT creates portal and auction access where applicable.",
      icon: Laptop,
    },
    {
      key: "training",
      label: "Training",
      description: "Training completed before final activation.",
      icon: GraduationCap,
    },
    {
      key: "active",
      label: "Active Member",
      description: "Member is fully onboarded and active.",
      icon: CheckCircle2,
    },
  ];

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-lg font-black text-slate-900">Workflow Timeline</h2>

      <div className="mt-5 grid gap-3">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const state = stepStatus(member, step.key);

          return (
            <div
              key={step.key}
              className={`rounded-2xl border p-4 ${stateClass(state)}`}
            >
              <div className="flex gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/70">
                  {state === "rejected" ? (
                    <XCircle className="h-5 w-5" />
                  ) : state === "current" ? (
                    <Clock className="h-5 w-5" />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                </div>

                <div>
                  <p className="text-sm font-black">
                    {index + 1}. {step.label}
                  </p>
                  <p className="mt-1 text-sm opacity-80">{step.description}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}